import {
  AiLoadingPanel,
  AiSlimBanner,
  CenteredLayout,
  Css,
  FormSectionLayout,
  HasIdAndName,
  defaultTestId,
  useComputed,
} from "@homebound/beam";
import { useCallback, useEffect, useState } from "react";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { AdditionalDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/AdditionalDetailsSection";
import { MaterialDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialDetailsSection";
import { VariantDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/VariantDetailsSection";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";

export type MaterialImportReviewStepProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
  /** `update` when the import went into a listing the user picked, rather than creating a new one. */
  mode?: "create" | "update";
};

/** `FormSection` ids its wrapper off its title, so this doubles as the Next Variant scroll anchor. */
const variantDetailsTitle = "Variant Details";

/**
 * The wizard's second step: the imported listing, in the same form the detail page edits.
 */
export function MaterialImportReviewStep(props: MaterialImportReviewStepProps) {
  const { items, brands, tags, markets, mode = "create" } = props;
  const { variants, selectedVariant, setSelectedVariant, addVariant, formState } = useMaterialListingContext();
  const initialized = useComputed(() => !!formState.id.value, [formState]);

  // Moving *off* a variant is what counts as having reviewed it, so the one the step opens on is still
  // outstanding and the banner can lead with the real number of imported variants.
  // We could have 2 potential sources of showing that a MV has been reviewed:
  // 1. What MVs have we already navigated to on this page? (current setup - requires separate tracking is weaker)
  // 2. Are there still any proposed AFPs for this MV? (might be a better indicator if we update AFPs in state as we view them. - Might be a better indicator on a refactor)
  const [reviewedIds, setReviewedIds] = useState<ReadonlySet<string>>(new Set());
  const selectedVariantId = selectedVariant?.id;
  useEffect(() => {
    if (!selectedVariantId) return;
    return () => setReviewedIds((prev) => (prev.has(selectedVariantId) ? prev : new Set(prev).add(selectedVariantId)));
  }, [selectedVariantId]);

  const outstanding = variants.filter((v) => !reviewedIds.has(v.id));
  // Sitting on the last one still outstanding finishes the sweep: there is nowhere left to move on to,
  // so holding it open would strand the count at one with a Next button that leads nowhere.
  const onLastOutstanding = outstanding.length === 1 && outstanding[0].id === selectedVariantId;
  const unreviewed = onLastOutstanding ? [] : outstanding;

  // Walks the strip so a reviewer can step through the variants without hunting for the thumbnails.
  // Skips the ones already clicked on and wraps, so repeated clicks land on each remaining variant
  // exactly once.
  const onNextVariant = useCallback(() => {
    const from = variants.findIndex((v) => v.id === selectedVariantId) + 1;
    const next = variants
      .map((_, i) => variants[(from + i) % variants.length])
      .find((v) => v.id !== selectedVariantId && !reviewedIds.has(v.id));
    if (next) setSelectedVariant(next.id);
    // Optional-called because jsdom leaves `scrollIntoView` off Element entirely.
    document.getElementById(defaultTestId(variantDetailsTitle))?.scrollIntoView?.({ behavior: "smooth" });
  }, [variants, selectedVariantId, setSelectedVariant, reviewedIds]);

  if (!initialized) {
    return (
      <CenteredLayout size="sm">
        <AiLoadingPanel title="We're importing your material..." omitBg />
      </CenteredLayout>
    );
  }

  return (
    <FormSectionLayout
      aiMode
      title={mode === "update" ? "Review Updates to Existing Material" : "Review Imported Material Details"}
      description={
        mode === "update"
          ? "We've applied the imported information to the existing material. Review the highlighted changes below and make any edits before saving."
          : "Please review the imported data highlighted below. You may edit or add to the form before saving."
      }
      initialFields={
        mode === "create" && variants.length > 1 ? (
          <div css={Css.mxPx(-24).$}>
            <AiSlimBanner
              title={reviewProgressTitle(unreviewed.length)}
              action={{ onClick: onNextVariant, label: "Next Variant", disabled: unreviewed.isEmpty }}
            />
          </div>
        ) : undefined
      }
      sections={[
        { title: "Material Details", fields: <MaterialDetailsSection items={items} brands={brands} /> },
        {
          title: variantDetailsTitle,
          actions: [{ label: "Add Variant", variant: "secondary", icon: "plus", onClick: addVariant }],
          fields: <VariantDetailsSection items={items} tags={tags} />,
        },
        { title: "Additional Details", fields: <AdditionalDetailsSection markets={markets} /> },
      ]}
    />
  );
}

/** Counts down as the reviewer works through the strip, so the banner reads as progress. */
function reviewProgressTitle(unreviewedCount: number): string {
  if (unreviewedCount === 0) return "All variants reviewed";
  if (unreviewedCount === 1) return "1 variant has been added and needs review";
  return `${unreviewedCount} variants have been added and need review`;
}
